import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart2, Settings } from 'lucide-react';

const STORAGE_KEY = 'lolarena_cookie_consent';

const defaultPrefs = {
  necessary: true,   // toujours activé
  analytics: false,
  preferences: false,
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Légère pause pour éviter le flash au chargement
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (chosenPrefs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...chosenPrefs,
      savedAt: new Date().toISOString(),
    }));
    setVisible(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, preferences: true });
  const rejectAll = () => save({ necessary: true, analytics: false, preferences: false });
  const saveCustom = () => save(prefs);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  if (!visible) return null;

  return (
    <div className={`cookie-banner ${expanded ? 'cookie-banner--expanded' : ''}`} role="dialog" aria-label="Consentement cookies">
      <div className="cookie-banner__inner">

        {/* En-tête */}
        <div className="cookie-banner__header">
          <div className="cookie-banner__title">
            <Cookie size={18} className="cookie-banner__icon" />
            <span>Cookies &amp; confidentialité</span>
          </div>
          <button className="cookie-banner__close" onClick={rejectAll} title="Refuser et fermer" aria-label="Refuser et fermer">
            <X size={16} />
          </button>
        </div>

        {/* Message principal */}
        <p className="cookie-banner__text">
          Nous utilisons des cookies pour assurer le bon fonctionnement de l'application et,
          avec votre accord, mesurer les performances.{' '}
          <Link to="/politique-confidentialite" className="cookie-banner__link" onClick={rejectAll}>
            Politique de confidentialité
          </Link>
        </p>

        {/* Détail des catégories (expandable) */}
        {expanded && (
          <div className="cookie-banner__categories">
            {/* Nécessaires */}
            <div className="cookie-cat">
              <div className="cookie-cat__header">
                <Shield size={15} className="cookie-cat__icon cookie-cat__icon--always" />
                <div className="cookie-cat__info">
                  <span className="cookie-cat__name">Nécessaires</span>
                  <span className="cookie-cat__desc">Authentification, session, sécurité. Indispensables au fonctionnement.</span>
                </div>
                <span className="cookie-cat__badge">Toujours actifs</span>
              </div>
            </div>

            {/* Analytiques */}
            <div className="cookie-cat">
              <div className="cookie-cat__header">
                <BarChart2 size={15} className="cookie-cat__icon" />
                <div className="cookie-cat__info">
                  <span className="cookie-cat__name">Analytiques</span>
                  <span className="cookie-cat__desc">Mesure d'audience anonymisée pour améliorer l'expérience.</span>
                </div>
                <button
                  className={`cookie-toggle ${prefs.analytics ? 'cookie-toggle--on' : ''}`}
                  onClick={() => toggle('analytics')}
                  role="switch"
                  aria-checked={prefs.analytics}
                  aria-label="Cookies analytiques"
                >
                  <span className="cookie-toggle__thumb" />
                </button>
              </div>
            </div>

            {/* Préférences */}
            <div className="cookie-cat">
              <div className="cookie-cat__header">
                <Settings size={15} className="cookie-cat__icon" />
                <div className="cookie-cat__info">
                  <span className="cookie-cat__name">Préférences</span>
                  <span className="cookie-cat__desc">Mémorisation de vos paramètres d'interface (région, thème…).</span>
                </div>
                <button
                  className={`cookie-toggle ${prefs.preferences ? 'cookie-toggle--on' : ''}`}
                  onClick={() => toggle('preferences')}
                  role="switch"
                  aria-checked={prefs.preferences}
                  aria-label="Cookies de préférences"
                >
                  <span className="cookie-toggle__thumb" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="cookie-banner__actions">
          <button
            className="cookie-btn cookie-btn--ghost"
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Réduire' : 'Personnaliser'}
          </button>

          {expanded && (
            <button className="cookie-btn cookie-btn--secondary" onClick={saveCustom}>
              Enregistrer mes choix
            </button>
          )}

          <button className="cookie-btn cookie-btn--outline" onClick={rejectAll}>
            Refuser
          </button>

          <button className="cookie-btn cookie-btn--primary" onClick={acceptAll}>
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  );
}
